import { AsyncPipe, Location } from '@angular/common'
import { ChangeDetectionStrategy, Component, HostListener, inject, ViewChild } from '@angular/core'
import { ActivatedRoute, CanDeactivateFn, Router } from '@angular/router'
import { catchError, forkJoin, map, of, switchMap } from 'rxjs'
import { BookingService } from '../services/booking.service'
import { ProductLibraryService } from '../services/product-library.service'
import { VenueService } from '../services/venue.service'
import { CoverChoicesEditComponent } from './cover-choices-edit.component'

@Component({
    selector: 'app-cover-choices-page',
    standalone: true,
    imports: [AsyncPipe, CoverChoicesEditComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './cover-choices-page.component.html',
    styles: [`
:host
    display: block
    height: 100vh
    height: 100dvh
    `],
})
export class CoverChoicesPageComponent {
    private route = inject(ActivatedRoute)
    private router = inject(Router)
    private location = inject(Location)
    private bookingService = inject(BookingService)
    private productLibraryService = inject(ProductLibraryService)
    private venueService = inject(VenueService)
    readonly individual = this.route.snapshot.data['individual'] === true
    @ViewChild(CoverChoicesEditComponent) editor?: CoverChoicesEditComponent

    @HostListener('window:beforeunload', ['$event'])
    beforeUnload(event: BeforeUnloadEvent) {
        if (this.editor?.hasUnsavedChanges) {
            event.preventDefault()
            event.returnValue = ''
        }
    }

    readonly page$ = this.route.paramMap.pipe(
        switchMap(params => this.bookingService.getBookingDetails(params.get('bookingId')!)),
        switchMap(details => forkJoin({
            venue: this.venueService.getWithId(details.venueId),
            library: this.productLibraryService.getForVenueId(details.venueId),
        }).pipe(map(({ venue, library }) => {
            const docket = details.docket
            const menu = docket && library.getMenu(docket.menuId)
            const name = this.individual ? this.route.snapshot.queryParamMap.get('name')?.trim() || null : null
            let coverIndex = Number(this.route.snapshot.paramMap.get('coverIndex'))
            if (this.individual && docket) {
                coverIndex = docket.coverChoices.findIndex(choice => choice.name?.trim().toLowerCase() === name?.toLowerCase())
                if (coverIndex === -1) coverIndex = docket.coverChoices.findIndex(choice => choice.selections.length === 0)
                if (coverIndex === -1) coverIndex = docket.coverChoices.length
            }
            if (!docket || !menu || (this.individual && !name) || !Number.isInteger(coverIndex) || coverIndex < 0 || coverIndex >= details.partySize) {
                throw new Error('Menu choices are unavailable')
            }
            return { details, docket, menu, venue, library, coverIndex, name, error: false as const }
        }))),
        catchError(() => of({ error: true as const })),
    )

    back() {
        if ((this.location.getState() as { menuChoicesEntry?: boolean } | null)?.menuChoicesEntry) {
            this.location.back()
        } else {
            this.router.navigate(['booking', this.route.snapshot.paramMap.get('bookingId'), 'menu', this.individual ? 'individual' : 'edit'], {
                replaceUrl: true,
                queryParams: this.individual ? { name: this.route.snapshot.queryParamMap.get('name') } : undefined,
            })
        }
    }
}

export const canLeaveMenuChoices: CanDeactivateFn<CoverChoicesPageComponent> = component => component.editor?.canLeave() ?? true
